import { Link, useLocation, useParams } from 'react-router-dom';
import { z } from 'zod';
import { ApiProblem } from '../api/problem.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { Card, CARD_SURFACE, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { RequestActions } from '../requests/request-actions.js';
import { RequestDocument } from '../requests/request-document.js';
import { RequestFields, RequestHistory } from '../requests/request-details.js';
import { useRequest, useRequestHistory } from '../requests/requests-api.js';
import { SubmitFailureFromForm } from '../requests/submit-after-create.js';
import { StatusBadge } from '../requests/status-badge.js';
import { useMe } from '../session/session.js';

/** Un identificador que no es un UUID no puede existir: «no encontrada» sin llamar a la API. */
export const NOT_A_REQUEST = new ApiProblem(404, 'No encontrada', 'NOT_FOUND', undefined, undefined, []);

/**
 * Detalle de una solicitud: `GET /requests/{id}` y su historial (W2), con las acciones del solicitante (W3).
 * El ámbito lo decide la API (404 o 403 se muestran con `ApiErrorView`). Con el aspecto del de la Power App
 * (`src/pages/request-details-page.tsx`): cabecera con la insignia y las secciones en tarjetas. Si se llega
 * desde el alta y falló el documento o el envío, muestra ese resultado (estado de la navegación).
 */
export function RequestDetailPage() {
  const { id = '' } = useParams();
  if (!z.uuid().safeParse(id).success) return <ApiErrorView error={NOT_A_REQUEST} />;
  return <RequestDetail id={id} />;
}

function RequestDetail({ id }: { id: string }) {
  const me = useMe();
  const location = useLocation();
  const request = useRequest(id);
  const history = useRequestHistory(id);

  if (request.isPending) return <output className="block text-muted-foreground">Cargando…</output>;
  if (request.isError) return <ApiErrorView error={request.error} />;
  const data = request.data;

  return (
    <div className="space-y-6 lg:px-6 lg:py-4">
      <Link to="/mis-solicitudes" className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
        Volver a mis solicitudes
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {data.requestNumber} <StatusBadge status={data.status} tokens />
          </span>
        }
        description="Consulta los datos, el documento y el historial de tu solicitud."
      />
      <SubmitFailureFromForm state={location.state} request={data} />
      <RequestActions request={data} />
      <Card>
        <CardHeader>
          <CardTitle>Datos del permiso</CardTitle>
        </CardHeader>
        <CardContent>
          <RequestFields request={data} className="" />
        </CardContent>
      </Card>
      {/* Adjuntar o retirar: solo quien la pidió y en borrador (la API decide). */}
      <RequestDocument request={data} editable={data.employeeId === me.id && data.status === 'DRAFT'} className={`p-5 ${CARD_SURFACE}`} />
      <Card>
        <CardHeader>
          <CardTitle id="historial">Historial</CardTitle>
        </CardHeader>
        <CardContent>
          {history.isPending ? <output className="block text-muted-foreground">Cargando el historial…</output> : null}
          {history.isError ? <ApiErrorView error={history.error} /> : null}
          {history.isSuccess ? <RequestHistory events={history.data} tokens /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
